/**
 * Enhanced KPI Cards Component
 * Sparkline、トレンド矢印、アニメーション付きのリッチなKPIカード
 * 3Dチルト、カウントアップ、グラデーション・グローエフェクト対応
 */

import {
  Card,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Badge,
  SimpleGrid,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconMinus,
  IconUsers,
  IconLogin,
  IconCalendar,
  IconSwimming,
} from '@tabler/icons-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';
import { motion } from 'framer-motion';
import Tilt from 'react-parallax-tilt';
import CountUp from 'react-countup';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import { getGlassStyle, getGradientGlowStyle } from '../styles/darkModeGlass';
import { KpiDetailModal } from './KpiDetailModal';
import { useKpiDetailData } from '../api/hooks';
import type { KpiWithMetrics, KpiMetrics } from '../types/enhanced';
import type { KpiType } from '../types/kpiDetail';

type EnhancedKpiCardProps = {
  label: string;
  data: KpiMetrics;
  icon: React.ComponentType<{ size?: number }>;
  color: string;
  onClick: () => void;
};

const MotionCard = motion(Card);

export const EnhancedKpiCard = ({
  label,
  data,
  icon: Icon,
  color,
  onClick,
}: EnhancedKpiCardProps) => {
  const { colorScheme } = useMantineColorScheme();
  const trendColor = data.trend === 'up' ? 'green' : data.trend === 'down' ? 'red' : 'gray';
  const TrendIcon =
    data.trend === 'up' ? IconArrowUpRight : data.trend === 'down' ? IconArrowDownRight : IconMinus;

  return (
    <Tilt
      tiltMaxAngleX={10}
      tiltMaxAngleY={10}
      perspective={1000}
      scale={1.02}
      transitionSpeed={400}
      glareEnable={true}
      glareMaxOpacity={0.3}
      glareColor="#ffffff"
      glarePosition="all"
      glareBorderRadius="8px"
    >
      <MotionCard
        padding="sm"
        radius="sm"
        shadow="sm"
        onClick={onClick}
        style={{
          ...getGlassStyle(colorScheme),
          ...getGradientGlowStyle(color, colorScheme),
          minHeight: '155px',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
        }}
        whileHover={{
          scale: 1.05,
        }}
        transition={{ duration: 0.3 }}
      >
        <Group justify="space-between" mb="xs">
          <Stack gap={2}>
            <Text size="xs" c="dimmed" fw={700} tt="uppercase">
              {label}
            </Text>
            <Group gap={4} align="baseline">
              <Text size="xl" fw={700} component="div">
                <CountUp
                  end={data.value}
                  duration={2}
                  separator=","
                  preserveValue={true}
                  useEasing={true}
                  easingFn={(t, b, c, d) => {
                    // easeOutExpo - スムーズな減速カーブ（t=dで正確に目標値に到達）
                    if (t === d) return b + c;
                    return c * (-Math.pow(2, (-10 * t) / d) + 1) + b;
                  }}
                />
              </Text>
              <Badge color={trendColor} variant="light" size="sm">
                <Group gap={2}>
                  <TrendIcon size={12} />
                  <Text size="xs">{data.percentage}</Text>
                </Group>
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {data.delta}
            </Text>
          </Stack>
          <ThemeIcon size="xl" radius="md" color={color} variant="light">
            <Icon size={24} />
          </ThemeIcon>
        </Group>

        {/* Sparkline */}
        <ResponsiveContainer width="100%" height={40}>
          <AreaChart
            data={data.sparkline.map((value, index) => ({ index, value }))}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <defs>
              <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={`var(--mantine-color-${color}-6)`} stopOpacity={0.6} />
                <stop
                  offset="95%"
                  stopColor={`var(--mantine-color-${color}-6)`}
                  stopOpacity={0.1}
                />
              </linearGradient>
            </defs>
            <Area
              type="bump"
              dataKey="value"
              stroke={`var(--mantine-color-${color}-6)`}
              strokeWidth={2}
              fill={`url(#gradient-${color})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </MotionCard>
    </Tilt>
  );
};

export const EnhancedKpiCards = ({ kpi }: { kpi: KpiWithMetrics }) => {
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedKpiType, setSelectedKpiType] = useState<KpiType | null>(null);

  // API経由でKPI詳細データを取得
  const { data: detailData, isLoading: isDetailLoading } = useKpiDetailData(selectedKpiType);

  const cards: Array<{
    key: keyof KpiWithMetrics;
    label: string;
    icon: React.ComponentType<{ size?: number }>;
    color: string;
  }> = [
    { key: 'active_members', label: 'アクティブ会員', icon: IconUsers, color: 'blue' },
    { key: 'today_checkins', label: '本日の入退室', icon: IconLogin, color: 'teal' },
    { key: 'monthly_attendances', label: '今月の出席数', icon: IconCalendar, color: 'violet' },
    { key: 'active_classes', label: 'アクティブクラス', icon: IconSwimming, color: 'cyan' },
  ];

  const handleCardClick = (kpiType: KpiType) => {
    setSelectedKpiType(kpiType);
    openModal();
  };

  const handleCloseModal = () => {
    closeModal();
    // モーダルを閉じた後にselectedKpiTypeをリセット（少し遅延を入れてアニメーション完了を待つ）
    setTimeout(() => setSelectedKpiType(null), 300);
  };

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {cards.map((card, index) => (
          <motion.div
            key={card.key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <EnhancedKpiCard
              label={card.label}
              data={kpi[card.key]}
              icon={card.icon}
              color={card.color}
              onClick={() => handleCardClick(card.key)}
            />
          </motion.div>
        ))}
      </SimpleGrid>
      <KpiDetailModal
        opened={modalOpened}
        onClose={handleCloseModal}
        data={detailData ?? null}
        isLoading={isDetailLoading}
      />
    </>
  );
};
